import { useEffect, useRef, useState, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface PresenceState {
  session_id: string;
  online_at: string;
  page: string;
  page_title?: string;
  user_agent?: string;
  device_type?: 'mobile' | 'tablet' | 'desktop';
  is_admin?: boolean;
}

interface VisitorInfo {
  key: string;
  data: PresenceState;
  isCurrentUser: boolean;
}

// Generate a consistent session ID per tab
const getSessionId = (): string => {
  if (typeof window === 'undefined') return '';
  let sessionId = sessionStorage.getItem('solr_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    sessionStorage.setItem('solr_session_id', sessionId);
  }
  return sessionId;
};

const getDeviceType = (): 'mobile' | 'tablet' | 'desktop' => {
  if (typeof window === 'undefined') return 'desktop';
  const ua = navigator.userAgent.toLowerCase();
  if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(ua)) return 'tablet';
  if (/mobile|iphone|ipod|android|blackberry|opera mini|iemobile/i.test(ua)) return 'mobile';
  return 'desktop';
};

const getPageTitle = (pathname: string): string => {
  const routes: Record<string, string> = {
    '/': 'Home', '/shop': 'Shop', '/cart': 'Cart', '/about': 'About',
    '/faq': 'FAQ', '/contact': 'Contact', '/ingredients': 'Ingredients',
    '/dashboard': 'Dashboard', '/admin': 'Admin', '/orders': 'Orders',
    '/auth': 'Sign In', '/terms': 'Terms', '/privacy': 'Privacy',
    '/shipping': 'Shipping', '/referral': 'Referral',
  };
  if (pathname.startsWith('/shop/')) return 'Product Details';
  if (pathname.startsWith('/orders/')) return 'Order Details';
  return routes[pathname] || pathname;
};

// Singleton channel manager to avoid duplicate subscriptions
let sharedChannel: ReturnType<typeof supabase.channel> | null = null;
let subscriberCount = 0;
let currentSessionId = '';
const syncListeners = new Set<(state: Record<string, any[]>) => void>();

function getOrCreateChannel(sessionId: string, isAdmin: boolean): ReturnType<typeof supabase.channel> {
  currentSessionId = sessionId;
  
  if (!sharedChannel) {
    sharedChannel = supabase.channel('site-visitors', {
      config: {
        presence: { key: sessionId },
      },
    });

    sharedChannel
      .on('presence', { event: 'sync' }, () => {
        const state = sharedChannel!.presenceState();
        syncListeners.forEach(listener => listener(state));
      })
      .on('presence', { event: 'join' }, () => {
        const state = sharedChannel!.presenceState();
        syncListeners.forEach(listener => listener(state));
      })
      .on('presence', { event: 'leave' }, () => {
        const state = sharedChannel!.presenceState();
        syncListeners.forEach(listener => listener(state));
      })
      .subscribe(async (status) => {
        if (status === 'SUBSCRIBED') {
          await sharedChannel!.track({
            session_id: sessionId,
            online_at: new Date().toISOString(),
            page: window.location.pathname,
            page_title: getPageTitle(window.location.pathname),
            user_agent: navigator.userAgent.substring(0, 100),
            device_type: getDeviceType(),
            is_admin: isAdmin,
          });
        }
      });
  }

  subscriberCount++;
  return sharedChannel;
}

function releaseChannel() {
  subscriberCount--;
  if (subscriberCount <= 0 && sharedChannel) {
    supabase.removeChannel(sharedChannel);
    sharedChannel = null;
    subscriberCount = 0;
    syncListeners.clear();
  }
}

/**
 * Hook to track user presence across the site
 */
export function usePresenceTracking() {
  const [activeCount, setActiveCount] = useState(0);
  const initialized = useRef(false);

  useEffect(() => {
    if (initialized.current) return;
    initialized.current = true;

    const sessionId = getSessionId();
    if (!sessionId) return;

    const channel = getOrCreateChannel(sessionId, false);

    const listener = (state: Record<string, any[]>) => {
      const nonAdminCount = Object.entries(state).filter(
        ([key, presences]) => {
          const p = presences[0] as PresenceState;
          return !p?.is_admin && !key.startsWith('admin_');
        }
      ).length;
      setActiveCount(Object.keys(state).length);
    };
    syncListeners.add(listener);

    // Update presence on page changes
    const updatePresence = async () => {
      if (sharedChannel && document.visibilityState === 'visible') {
        try {
          await sharedChannel.track({
            session_id: sessionId,
            online_at: new Date().toISOString(),
            page: window.location.pathname,
            page_title: getPageTitle(window.location.pathname),
            user_agent: navigator.userAgent.substring(0, 100),
            device_type: getDeviceType(),
          });
        } catch (e) { /* silent */ }
      }
    };

    document.addEventListener('visibilitychange', updatePresence);
    const keepAlive = setInterval(() => {
      if (document.visibilityState === 'visible') updatePresence();
    }, 25000);

    return () => {
      initialized.current = false;
      syncListeners.delete(listener);
      document.removeEventListener('visibilitychange', updatePresence);
      clearInterval(keepAlive);
      releaseChannel();
    };
  }, []);

  return activeCount;
}

/**
 * Hook for admin dashboard to monitor all visitors
 */
export function useAdminPresenceMonitor() {
  const [visitors, setVisitors] = useState<VisitorInfo[]>([]);
  const [activeCount, setActiveCount] = useState(0);

  useEffect(() => {
    const sessionId = getSessionId();
    if (!sessionId) return;

    const channel = getOrCreateChannel(sessionId, true);

    const listener = (state: Record<string, any[]>) => {
      const visitorList: VisitorInfo[] = Object.entries(state).map(([key, presences]) => {
        const p = (presences[0] || {}) as PresenceState;
        return {
          key,
          data: {
            session_id: p.session_id || key,
            online_at: p.online_at || new Date().toISOString(),
            page: p.page || '/',
            page_title: p.page_title || getPageTitle(p.page || '/'),
            user_agent: p.user_agent,
            device_type: p.device_type || 'desktop',
            is_admin: p.is_admin || key.startsWith('admin_'),
          },
          isCurrentUser: key === sessionId,
        };
      });

      const regularVisitors = visitorList.filter(v => !v.data.is_admin);
      setVisitors(regularVisitors);
      setActiveCount(regularVisitors.length);
    };
    syncListeners.add(listener);

    return () => {
      syncListeners.delete(listener);
      releaseChannel();
    };
  }, []);

  const stats = {
    total: activeCount,
    byPage: visitors.reduce((acc, v) => {
      const page = v.data.page_title || v.data.page;
      acc[page] = (acc[page] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
    byDevice: visitors.reduce((acc, v) => {
      const device = v.data.device_type || 'desktop';
      acc[device] = (acc[device] || 0) + 1;
      return acc;
    }, {} as Record<string, number>),
  };

  return { visitors, activeCount, stats };
}

export default usePresenceTracking;
